import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import TextWithLabel from "../../../controls/TextWithLabel";

function formatDateTime(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return {
      dateText: value,
      timeText: "",
    };
  }

  return {
    dateText: date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    timeText: date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
}

function formatBarcodeSerial(value) {
  if (!value) return "N/A";
  const barcode = String(value);
  if (barcode.length <= 15) return barcode;
  return `...${barcode.slice(-15)}`;
}

function getActionBadgeClass(action) {
  const text = (action || "").toLowerCase();
  if (text.includes("deletion") || text.includes("reversal")) {
    return "bg-red-100 text-red-700";
  }
  if (text.includes("creation")) {
    return "bg-green-100 text-green-700";
  }
  if (text.includes("update") || text.includes("adjustment")) {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-slate-100 text-slate-700";
}

function formatAmountWithUom(value, unitOfMeasure) {
  if (value == null) return "N/A";
  const uom = unitOfMeasure ? ` ${unitOfMeasure}` : "";
  return `${value}${uom}`;
}

function historyRowKey(event, index) {
  if (event.id != null) return String(event.id);
  const barcode = event.barcode != null ? String(event.barcode) : "na";
  const created = event.created_at != null ? String(event.created_at) : "";
  return `${barcode}-${created}-${index}`;
}

function getManufacturedProductId(event) {
  const fromMeta = event.metadata?.manufactured_product_id;
  if (fromMeta != null) return fromMeta;
  const ctx = event.context?.manufactured_product ?? event.context?.manufacturing;
  if (ctx && typeof ctx === "object" && ctx.id != null) return ctx.id;
  return null;
}

function contextKeysBeyondInvoice(context) {
  if (!context || typeof context !== "object") return [];
  return Object.keys(context).filter((k) => k !== "invoice" && context[k] != null);
}

function hasExpandableHistoryDetail(event) {
  if (event.context?.invoice) return true;
  if (event.metadata && Object.keys(event.metadata).length > 0) return true;
  if (getManufacturedProductId(event) != null) return true;
  if (contextKeysBeyondInvoice(event.context).length > 0) return true;
  return false;
}

function HistoryInvoiceFullPanel({ invoice: contextInvoice }) {
  if (!contextInvoice) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Invoice Context
        </p>
        {contextInvoice.id ? (
          <Link
            className="rounded-md bg-main-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
            to={`/receiving-invoices/${contextInvoice.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </Link>
        ) : null}
      </div>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TextWithLabel
          label="Invoice Number"
          text={contextInvoice.invoice_number || "N/A"}
        />
        <TextWithLabel
          label="Supplier Name"
          text={contextInvoice.supplier_name || "N/A"}
        />
        <TextWithLabel label="Date" text={contextInvoice.date || "N/A"} />
        <TextWithLabel label="Location" text={contextInvoice.location || "N/A"} />
      </div>
    </div>
  );
}

function HistoryManufacturingPanel({ event }) {
  const productId = getManufacturedProductId(event);
  const ctxProduct =
    event.context?.manufactured_product ||
    event.context?.manufacturing ||
    null;
  const meta = event.metadata;

  if (productId == null && !ctxProduct && !meta?.manufactured_product_ingredient_id) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Production / manufacturing
        </p>
        {productId != null ? (
          <Link
            className="rounded-md bg-main-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
            to={`/manufactured-products/${productId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </Link>
        ) : null}
      </div>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {productId != null ? (
          <TextWithLabel label="Manufactured product ID" text={String(productId)} />
        ) : null}
        {ctxProduct && typeof ctxProduct === "object" ? (
          <>
            {ctxProduct.name != null ? (
              <TextWithLabel label="Name" text={String(ctxProduct.name)} />
            ) : null}
            {ctxProduct.batch_number != null ? (
              <TextWithLabel label="Batch" text={String(ctxProduct.batch_number)} />
            ) : null}
          </>
        ) : null}
        {meta?.manufactured_product_ingredient_id != null ? (
          <TextWithLabel
            label="Ingredient link ID"
            text={String(meta.manufactured_product_ingredient_id)}
          />
        ) : null}
        {meta?.flow != null ? <TextWithLabel label="Flow" text={String(meta.flow)} /> : null}
        {meta?.delta_used_weight != null ? (
          <TextWithLabel label="Delta used weight" text={String(meta.delta_used_weight)} />
        ) : null}
        {meta?.delta_in_stock != null ? (
          <TextWithLabel label="Delta in stock" text={String(meta.delta_in_stock)} />
        ) : null}
      </div>
    </div>
  );
}

// function HistoryOtherContextPanel({ event }) {
//   const keys = contextKeysBeyondInvoice(event.context).filter((k) => {
//     const v = event.context[k];
//     if (v === event.context?.invoice) return false;
//     if (k === "manufactured_product" || k === "manufacturing") return false;
//     return true;
//   });
//   if (keys.length === 0) return null;

//   return (
//     <div className="rounded-lg border border-gray-200 bg-white p-3">
//       <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
//         Additional context
//       </p>
//       <pre className="mt-2 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
//         {JSON.stringify(
//           keys.reduce((acc, k) => {
//             acc[k] = event.context[k];
//             return acc;
//           }, {}),
//           null,
//           2,
//         )}
//       </pre>
//     </div>
//   );
// }

// function HistoryMetadataPanel({ metadata }) {
//   if (!metadata) return null;
//   return (
//     <div className="rounded-lg border border-gray-200 bg-white p-3">
//       <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Metadata</p>
//       <pre className="mt-2 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
//         {JSON.stringify(metadata, null, 2)}
//       </pre>
//     </div>
//   );
// }

function HistoryEventExpansion({ event }) {
  return (
    <div className="space-y-3 border-t border-gray-200 bg-gray-50/80 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TextWithLabel
          label="Barcode Serial"
          text={formatBarcodeSerial(event.barcode)}
        />
        <TextWithLabel label="Unit Cost" text={event.unit_cost ?? "N/A"} />
      </div>
      <HistoryInvoiceFullPanel invoice={event.context?.invoice} />
      <HistoryManufacturingPanel event={event} />
      {/* <HistoryOtherContextPanel event={event} /> */}
      {/* <HistoryMetadataPanel metadata={event.metadata} /> */}
    </div>
  );
}

function HistoryContextSummaryCell({ event }) {
  const invoice = event.context?.invoice;
  const productId = getManufacturedProductId(event);
  const ctxProduct = event.context?.manufactured_product || event.context?.manufacturing;
  const extraKeys = contextKeysBeyondInvoice(event.context).filter(
    (k) => k !== "manufactured_product" && k !== "manufacturing",
  );

  const linkClass =
    "shrink-0 rounded-md bg-main-purple px-2 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-indigo-600";

  return (
    <div className="flex min-w-[11rem] max-w-md flex-col gap-2 text-xs">
      {invoice ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900">
                {invoice.invoice_number || "—"}
              </p>
              <p
                className="truncate text-gray-600"
                title={invoice.supplier_name || ""}
              >
                {invoice.supplier_name || "—"}
              </p>
            </div>
            {invoice.id ? (
              <Link
                className={linkClass}
                to={`/receiving-invoices/${invoice.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Invoice
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {productId != null || ctxProduct ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900">Production</p>
              <p className="truncate text-gray-600" title={ctxProduct?.name || ""}>
                {ctxProduct?.name
                  ? String(ctxProduct.name)
                  : productId != null
                    ? `Product #${productId}`
                    : "—"}
              </p>
            </div>
            {productId != null ? (
              <Link
                className={linkClass}
                to={`/manufactured-products/${productId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* {extraKeys.length > 0 ? (
        <p className="text-gray-600">
          Additional context ({extraKeys.length}) — expand row for details.
        </p>
      ) : null} */}

      {!invoice && productId == null && !ctxProduct && extraKeys.length === 0 ? (
        <span className="text-gray-400">—</span>
      ) : null}
    </div>
  );
}

export default function ReceivingItemLookupResult({ item, invoice, histories }) {
  const itemDateTime = formatDateTime(item?.created_at);
  const lastScanDateTime = formatDateTime(histories?.[0]?.created_at);

  const [expandedRows, setExpandedRows] = useState(null);

  const rowsWithKeys = useMemo(
    () =>
      (histories || []).map((event, index) => ({
        ...event,
        _rowKey: historyRowKey(event, index),
      })),
    [histories],
  );

  useEffect(() => {
    setExpandedRows(null);
  }, [item?.id, item?.barcode]);

  const allowExpansion = (rowData) => hasExpandableHistoryDetail(rowData);

  const whenBody = (row) => {
    const dt = formatDateTime(row.created_at);
    return (
      <div>
        <p className="text-xs font-semibold text-gray-800">{dt.dateText}</p>
        {dt.timeText ? <p className="text-xs text-gray-500">{dt.timeText}</p> : null}
      </div>
    );
  };

  const actionBody = (row) => (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getActionBadgeClass(row.action)}`}
    >
      {(row.action || "unknown").replace(/_/g, " ")}
    </span>
  );

  const typeBody = (row) => (
    <span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
      {(row.type || "unknown").replace(/_/g, " ")}
    </span>
  );

  const reasonBody = (row) => {
    const text = row.reason || "—";
    const truncated = text.length > 72 ? `${text.slice(0, 69)}…` : text;
    return (
      <span className="text-gray-700" title={text.length > 72 ? text : undefined}>
        {truncated}
      </span>
    );
  };

  const contextBody = (row) => <HistoryContextSummaryCell event={row} />;

  const rowExpansionTemplate = (rowData) => <HistoryEventExpansion event={rowData} />;

  return (
    <div className="space-y-4 py-4">
      <div className="card-custom rounded-xl border border-gray-200 p-3 sm:p-4">
        <h2 className="text-sm font-semibold text-gray-900">Resolved Item</h2>
        <div className="mt-2 grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-start lg:gap-4">
          <div className="min-w-0 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:col-span-8 xl:grid-cols-4">
            <TextWithLabel
              label="ID"
              text={item?.id ?? "N/A"}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Barcode Serial"
              text={formatBarcodeSerial(item?.barcode)}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Product Name"
              text={item?.product_name || "N/A"}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Lot Number"
              text={item?.lot_number || "N/A"}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Supplier Name"
              text={item?.supplier_name || "N/A"}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Total Unit"
              text={
                item?.received_amount != null
                  ? `${item.received_amount}${item.unit_of_measure ? ` ${item.unit_of_measure}` : ""}`
                  : "N/A"
              }
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="In Stock"
              text={
                item?.in_stock != null
                  ? `${item.in_stock}${item.unit_of_measure ? ` ${item.unit_of_measure}` : ""}`
                  : "N/A"
              }
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Last Scan Date"
              text={
                lastScanDateTime.timeText
                  ? `${lastScanDateTime.dateText}, ${lastScanDateTime.timeText}`
                  : lastScanDateTime.dateText
              }
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Created At"
              text={
                itemDateTime.timeText
                  ? `${itemDateTime.dateText}, ${itemDateTime.timeText}`
                  : itemDateTime.dateText
              }
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 sm:p-2.5 lg:col-span-4 lg:h-min">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Invoice Context
              </p>
              {invoice?.id ? (
                <Link
                  className="rounded-md bg-main-purple px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
                  to={`/receiving-invoices/${invoice.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </Link>
              ) : null}
            </div>
            <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-2">
              <TextWithLabel
                label="Invoice Number"
                text={invoice?.invoice_number || "N/A"}
                labelClass="text-[11px] uppercase tracking-wide text-gray-500"
                textClass="text-sm text-gray-900"
              />
              <TextWithLabel
                label="Supplier Name"
                text={invoice?.supplier_name || "N/A"}
                labelClass="text-[11px] uppercase tracking-wide text-gray-500"
                textClass="text-sm text-gray-900"
              />
              <TextWithLabel
                label="Date"
                text={invoice?.date || "N/A"}
                labelClass="text-[11px] uppercase tracking-wide text-gray-500"
                textClass="text-sm text-gray-900"
              />
              <TextWithLabel
                label="Location"
                text={invoice?.location || "N/A"}
                labelClass="text-[11px] uppercase tracking-wide text-gray-500"
                textClass="text-sm text-gray-900"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">History Timeline</h3>
          <span className="text-sm text-gray-500">
            {histories.length} event{histories.length === 1 ? "" : "s"}
          </span>
        </div>
        {histories.length === 0 ? (
          <div className="card-custom rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No history rows were returned for this barcode.
          </div>
        ) : (
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden card-custom bg-white">
                  <DataTable
                    value={rowsWithKeys}
                    dataKey="_rowKey"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                    stripedRows
                    tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                    cellClassName="p-2 border-b border-gray-200 text-sm align-top"
                  >
                    <Column body={whenBody} header="When" style={{ width: "8.5rem" }} />
                    <Column body={actionBody} header="Action" style={{ width: "9rem" }} />
                    <Column body={typeBody} header="Type" style={{ width: "11rem" }} />
                    <Column
                      field="operator.label"
                      header="Operator"
                      body={(r) => r.operator?.label || "—"}
                    />
                    <Column
                      header="Total"
                      body={(r) =>
                        formatAmountWithUom(r.received_amount, r.unit_of_measure)
                      }
                      style={{ textAlign: "right", width: "6rem" }}
                    />
                    <Column
                      header="In stock"
                      body={(r) => formatAmountWithUom(r.in_stock, r.unit_of_measure)}
                      style={{ textAlign: "right", width: "6rem" }}
                    />
                    <Column
                      header="Used wgt."
                      body={(r) => formatAmountWithUom(r.used_weight, r.unit_of_measure)}
                      style={{ textAlign: "right", width: "6.5rem" }}
                    />
                    <Column
                      header="Reason"
                      body={reasonBody}
                      style={{ minWidth: "10rem", maxWidth: "14rem" }}
                    />
                    <Column
                      header="Context"
                      body={contextBody}
                      style={{ minWidth: "12rem" }}
                    />
                    <Column expander={allowExpansion} style={{ width: "3rem" }} />
                  </DataTable>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
